"use client";

import startGameSession from "@casino/actions/Games/startSession";
import GameAreaPlaceholder from "@casino/ui/Games/GameAreaPlaceholder";
import GameLoadError from "@casino/ui/Games/GameLoadError";
import gameAreaBoxClasses from "@casino/ui/Games/gameAreaBoxClasses";
import { useGamePageData } from "@casino/ui/Games/hooks";
import NoFunModeMessage from "@casino/ui/Games/NoFunModeMessage";
import type { WebComponentGameConfig } from "@casino/ui/Games/WebComponentGame";
import WebComponentGame from "@casino/ui/Games/WebComponentGame";
import webComponentBoxClasses from "@casino/ui/Games/webComponentBoxClasses";
import webComponentLoadedBoxClasses from "@casino/ui/Games/webComponentLoadedBoxClasses";
import type { ComponentProps, FC } from "react";
import * as React from "react";
import FullscreenBackdrop from "./FullscreenBackdrop";
import FullscreenExitOverlay from "./FullscreenExitOverlay";
import fullscreenBoxClasses from "./fullscreenBoxClasses";
import fullscreenStyle from "./fullscreenStyle";

type Props = ComponentProps<"div"> & {
  gameId: string;
  webComponent?: WebComponentGameConfig;
  bgColor?: string;
  hasFunMode?: boolean;
};

const GameArea: FC<Props> = ({
  gameId,
  webComponent,
  bgColor,
  hasFunMode,
  ...props
}) => {
  const {
    iframeRef,
    isFullscreen,
    isFunMode,
    isLoggedIn,
    inGameCurrency,
    walletCurrency,
    t,
  } = useGamePageData();
  const [iframeSrc, setUrl] = React.useState<string | null>(null);
  const [sessionError, setSessionError] = React.useState(false);
  const [isMounted, setIsMounted] = React.useState(true);
  const [isWebComponentReady, setIsWebComponentReady] = React.useState(false);
  const showNoFunMode = !hasFunMode && (isFunMode || !isLoggedIn);

  // <Activity> tears down effects without unmounting, so without this the hidden game keeps running and playing audio.
  // Set it back unconditionally: this closure's isMounted is the first render's, so after a
  // cleanup (Strict Mode's double effect run, an Activity re-show) a guarded reset never fires.
  React.useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  const boxStyle = {
    backgroundColor: bgColor,
    ...fullscreenStyle(isFullscreen),
  };
  const boxClasses = isFullscreen ? fullscreenBoxClasses : gameAreaBoxClasses;
  const sizedWebComponentClasses = isWebComponentReady
    ? webComponentLoadedBoxClasses
    : webComponentBoxClasses;
  const webComponentClasses = isFullscreen
    ? fullscreenBoxClasses
    : sizedWebComponentClasses;

  const requestId = React.useRef(0);

  const launchSession = React.useCallback(() => {
    if (!gameId || webComponent || showNoFunMode) return;

    const id = ++requestId.current;
    setUrl(null);
    setSessionError(false);
    (async () => {
      const data = await startGameSession({
        gameId,
        isFunMode,
        inGameCurrency,
      });
      if (id !== requestId.current) return;
      setUrl(data?.url ?? null);
      setSessionError(!data?.url);
    })();
  }, [
    gameId,
    isFunMode,
    inGameCurrency,
    walletCurrency,
    webComponent,
    showNoFunMode,
  ]);

  React.useEffect(() => {
    launchSession();
  }, [launchSession]);

  React.useEffect(() => {
    setIsWebComponentReady(false);
  }, [gameId]);

  if (!gameId) return null;

  if (!isMounted) return null;

  if (showNoFunMode) {
    return (
      <div
        className={`${boxClasses} relative rounded-lg`}
        style={boxStyle}
        {...props}
      >
        <NoFunModeMessage />
      </div>
    );
  }

  if (webComponent) {
    return (
      <>
        <FullscreenBackdrop />
        <div
          className={webComponentClasses}
          data-testid="web-component-box"
          style={boxStyle}
          {...props}
        >
          <WebComponentGame
            {...webComponent}
            onReadyChange={setIsWebComponentReady}
          />
        </div>
        <FullscreenExitOverlay />
      </>
    );
  }

  if (sessionError) {
    return (
      <>
        <FullscreenBackdrop />
        <div
          className={`${boxClasses} relative rounded-lg`}
          style={boxStyle}
          {...props}
        >
          <GameLoadError onRetry={launchSession} />
        </div>
        <FullscreenExitOverlay />
      </>
    );
  }

  if (!iframeSrc) {
    return <GameAreaPlaceholder {...props} />;
  }

  return (
    <>
      <FullscreenBackdrop />
      <iframe
        ref={iframeRef}
        title={t("game.iframe.title", "Game")}
        src={iframeSrc}
        className={`${boxClasses} rounded-lg`}
        allow="autoplay; fullscreen"
        style={boxStyle}
      />
      <FullscreenExitOverlay />
    </>
  );
};

export default GameArea;
